<section class="bg-white py-20">
  <div class="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">

    <!-- Text Content -->
    <div class="space-y-8 text-center md:text-left">

      <!-- Headline -->
      <h1 class="text-4xl md:text-5xl font-bold leading-tight">
        Get ISO 27001 & ISO 42001 Certified Without the Complexity
      </h1>

      <!-- Sub-headline -->
      <p class="text-lg text-gray-600">
        Practical, auditor-led support to take you from gap assessment to certification — fast, structured, and without unnecessary overhead.
      </p>

      <!-- Bullets -->
      <div class="space-y-2 text-gray-700 text-left inline-block">
        <div>✔ Led by experienced ISO 27001 & ISO 42001 certification auditors</div>
        <div>✔ Clear, step-by-step approach</div>
        <div>✔ No consultant lock-in</div>
      </div>

      <!-- CTAs -->
      <div class="flex justify-center md:justify-start gap-4 flex-wrap">

        <button
          class="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition cursor-pointer"
          routerLink="/gap-check/intro"
        >
          Where do I start?
        </button>

        <button
          routerLink="/pricing"
          class="border border-blue-600 text-blue-600 px-6 py-3 rounded-lg hover:bg-blue-50 transition cursor-pointer"
        >
          View Pricing
        </button>

      </div>
    </div>

    <!-- Hero Image -->
    <div class="relative flex justify-center">
      <img
        src="assets/home_image_hero.jpg"
        alt="ISO 27001 Compliance Illustration"
        class="w-4/5 rounded-2xl shadow-xl object-cover"
      />
    </div>

  </div>
</section>

<!-- WHAT WE DO -->
<section class="bg-gray-50 py-16">
  <div class="max-w-6xl mx-auto px-6 space-y-12">

    <!-- Section Header -->
    <div class="text-center max-w-3xl mx-auto space-y-6">
      <h2 class="text-3xl md:text-4xl font-bold">What We Do</h2>

      <p class="text-lg text-gray-600">
        AuditFixers helps organisations achieve ISO/IEC 27001 and ISO/IEC 42001 compliance through practical, auditor-led guidance—eliminating unnecessary complexity, over-engineering, and long-term consulting dependency.
      </p>

      <p class="text-lg text-gray-600">
        We combine <b>auditor-led expertise, practical remediation,</b> and <b>clear prioritisation</b> so you can focus on what matters most—achieving certification, strengthening governance, and reducing business risk.
      </p>
    </div>

    <!-- Feature Block 1 -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">

      <div class="space-y-4">
        <h3 class="text-2xl font-bold">Independent Remediation, Done Right</h3>

        <p class="text-gray-700">
          We don’t just identify gaps — we help you fix them.
        </p>

        <p class="text-gray-700">
          Our approach is built around <b>real audit expectations</b>, ensuring every action stands up to certification scrutiny. Whether remote or hybrid, we work alongside your team to deliver <b>clear, implementable outcomes, </b> not theoretical advice.
        </p>
      </div>

      <div class="flex justify-center">
        <img
          src="assets/home_image_1.png"
          alt="Independent Remediation"
          class="w-full max-w-md object-contain"
        />
      </div>

    </div>

    <!-- Feature Block 2 -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">

      <div class="order-2 md:order-1 flex justify-center">
        <img
          src="assets/home_image_2.png"
          alt="How We Add Value"
          class="w-full max-w-md object-contain"
        />
      </div>

      <div class="order-1 md:order-2 space-y-4">

        <h3 class="text-2xl font-bold">How We Add Value</h3>

        <p class="text-gray-700">
          Unlike traditional consulting, we focus on <b>execution and outcomes</b>:
        </p>

        <ul class="list-disc list-inside text-gray-700 space-y-2">
          <li>Translate gaps into <b>clear, actionable fixes</b></li>
          <li>Provide <b>ready-to-use templates and documentation</b></li>
          <li>Align everything to what <b>certification bodies actually expect</b></li>
          <li>Reduce time, cost, and internal effort</li>
          <li>Build internal capability — not dependency</li>
        </ul>

      </div>

    </div>

  </div>
</section>

<!-- WHY AUDITFIXERS -->
<section class="bg-white py-20">
  <div class="max-w-6xl mx-auto px-6 space-y-14">

    <!-- Header -->
    <div class="text-center max-w-3xl mx-auto space-y-6">
      <h2 class="text-3xl md:text-4xl font-bold">Why AuditFixers</h2>
    </div>

    <!-- Feature Grid -->
    <div class="grid md:grid-cols-2 gap-12">

      <!-- Feature 1 -->
      <div class="space-y-4">
        <h3 class="font-semibold text-xl text-gray-900">
          Transparent, SME-friendly pricing
        </h3>

        <p class="text-gray-700 leading-relaxed">
          Clear scope, defined deliverables, and predictable costs — with no lock-in. 
          You only pay for what you need, when you need it.
        </p>

        <div class="w-full h-64 mt-4 rounded-xl bg-gray-50 flex items-center justify-center shadow">
          <img
            src="assets/home_image_why1.png"
            alt="Transparent pricing"
            class="w-full max-w-xs object-contain"
          />
        </div>
      </div>

      <!-- Feature 2 -->
      <div class="space-y-4">
        <h3 class="font-semibold text-xl text-gray-900">
          Auditor-ready documentation
        </h3>

        <p class="text-gray-700 leading-relaxed">
          Policies, risk registers, Statement of Applicability, and supporting evidence — 
          all structured to meet certification body expectations and withstand audit scrutiny.
        </p>

        <div class="w-full h-64 mt-4 rounded-xl bg-gray-50 flex items-center justify-center shadow">
          <img
            src="assets/home_image_why4.png"
            alt="Auditor-ready documentation"
            class="w-full max-w-xs object-contain"
          />
        </div>
      </div>

      <!-- Feature 3 -->
      <div class="space-y-4">
        <h3 class="font-semibold text-xl text-gray-900">
          Outcome-focused, not advisory-heavy
        </h3>

        <p class="text-gray-700 leading-relaxed">
          We prioritise implementation over theory — translating gaps into practical, 
          audit-ready actions that move you towards certification.
        </p>

        <div class="w-full h-64 mt-4 rounded-xl bg-gray-50 flex items-center justify-center shadow">
          <img
            src="assets/home_image_why3.png"
            alt="Outcome-focused delivery"
            class="w-full max-w-xs object-contain"
          />
        </div>
      </div>

      <!-- Feature 4 -->
      <div class="space-y-4">
        <h3 class="font-semibold text-xl text-gray-900">
          Built for certification success
        </h3>

        <p class="text-gray-700 leading-relaxed">
          Everything we deliver is aligned with ISO/IEC 27001:2022 and ISO/IEC 42001:2023 requirements, 
          as well as real-world audit expectations — reducing rework, delays, and certification risk.
        </p>

        <div class="w-full h-64 mt-4 rounded-xl bg-gray-50 flex items-center justify-center shadow">
          <img
            src="assets/home_image_why2.png"
            alt="Certification success"
            class="w-full max-w-xs object-contain"
          />
        </div>
      </div>

    </div>
  </div>
</section>

<!-- WHO WE HELP -->
<section class="bg-gray-50 py-20">
  <div class="max-w-6xl mx-auto px-6 space-y-14">
    <!-- Header -->
    <div class="text-center max-w-3xl mx-auto space-y-6">
      <h2 class="text-3xl md:text-4xl font-bold">Who We Help</h2>

      <p class="text-lg text-gray-600">
        AuditFixers is a strong fit for Organisations preparing for ISO 27001 and ISO 42001 certification,
        procurement compliance, or audit readiness.
      </p>
    </div>

    <!-- Client Segment Grid -->
    <div class="grid md:grid-cols-3 gap-8 text-center">
      <div class="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition">
        <img
          src="assets/icons/cloud.svg"
          class="w-14 h-14 mx-auto mb-4"
          alt="SaaS Technology Companies"
        />

        <h3 class="font-semibold text-xl mb-3">SaaS & Technology Companies</h3>

        <p class="text-gray-600 text-sm">
          Software and digital businesses handling customer or platform data.
        </p>
      </div>

      <div class="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition">
        <img
          src="assets/icons/briefcase.svg"
          class="w-14 h-14 mx-auto mb-4"
          alt="BPO Outsourcing Providers"
        />

        <h3 class="font-semibold text-xl mb-3">BPO & Outsourcing Providers</h3>

        <p class="text-gray-600 text-sm">
          Service Organisations processing client operational or personal information.
        </p>
      </div>

      <div class="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition">
        <img
          src="assets/icons/heart.svg"
          class="w-14 h-14 mx-auto mb-4"
          alt="Healthcare Clinical Organisations"
        />

        <h3 class="font-semibold text-xl mb-3">Healthcare & Clinical Organisations</h3>

        <p class="text-gray-600 text-sm">
          Medical and health institutions managing sensitive patient data.
        </p>
      </div>

      <div class="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition">
        <img
          src="assets/icons/bank.svg"
          class="w-14 h-14 mx-auto mb-4"
          alt="Fintech Regulated Services"
        />

        <h3 class="font-semibold text-xl mb-3">Fintech & Regulated Services</h3>

        <p class="text-gray-600 text-sm">
          Financial technology and compliance-driven Organisations.
        </p>
      </div>

      <div class="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition">
        <img src="assets/icons/building.svg" class="w-14 h-14 mx-auto mb-4" alt="SMEs Employees" />

        <h3 class="font-semibold text-xl mb-3">SMEs (10–250 Employees)</h3>

        <p class="text-gray-600 text-sm">
          Businesses processing customer, operational, or personal data.
        </p>
      </div>

      <div class="bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition">
        <img
          src="assets/icons/checklist.svg"
          class="w-14 h-14 mx-auto mb-4"
          alt="Procurement Audit Bidders"
        />

        <h3 class="font-semibold text-xl mb-3">Procurement & Audit Bidders</h3>

        <p class="text-gray-600 text-sm">
          Organisations preparing for client security requirements or tender compliance.
        </p>
      </div>
    </div>
  </div>
</section>

<!-- HOW IT WORKS -->
<section class="bg-gray-50 py-20">
  <div class="max-w-6xl mx-auto px-6 space-y-14">

    <!-- Header -->
    <div class="text-center max-w-3xl mx-auto space-y-6">
      <h2 class="text-3xl md:text-4xl font-bold">How It Works</h2>

      <p class="text-lg text-gray-600">
        Our streamlined process takes you from gap identification to certification readiness with clear, structured steps.
      </p>
    </div>

    <!-- Workflow -->
    <div class="relative">

      <!-- CONNECTOR LINE (fixed padding so it doesn't touch edges) -->
      <div class="hidden md:block absolute top-8 left-[10%] right-[10%] h-1 bg-gray-200"></div>

      <div class="grid grid-cols-1 md:grid-cols-5 gap-10 relative">

        <!-- Step 1 -->
        <div class="text-center relative">
          <div class="w-16 h-16 mx-auto rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg relative z-10">
            1
          </div>
          <h3 class="mt-4 font-semibold">Free Gap Check</h3>
        </div>

        <!-- Step 2 -->
        <div class="text-center relative">
          <div class="w-16 h-16 mx-auto rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg relative z-10">
            2
          </div>
          <h3 class="mt-4 font-semibold">Build Your ISMS</h3>
        </div>

        <!-- Step 3 -->
        <div class="text-center relative">
          <div class="w-16 h-16 mx-auto rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg relative z-10">
            3
          </div>
          <h3 class="mt-4 font-semibold">Implement Controls</h3>
        </div>

        <!-- Step 4 -->
        <div class="text-center relative">
          <div class="w-16 h-16 mx-auto rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg relative z-10">
            4
          </div>
          <h3 class="mt-4 font-semibold">
            Internal Audit & Evidence Preparation
          </h3>
        </div>

        <!-- Step 5 -->
        <div class="text-center relative">
          <div class="w-16 h-16 mx-auto rounded-full bg-blue-600 text-white flex items-center justify-center font-bold text-lg relative z-10">
            5
          </div>
          <h3 class="mt-4 font-semibold">Certification Support</h3>
        </div>

      </div>

    </div>

  </div>
</section>

<!-- WHAT YOU GET -->
<section class="bg-gray-50 py-24">
  <div class="max-w-6xl mx-auto px-6 space-y-14">
    <!-- Header -->
    <div class="text-center max-w-3xl mx-auto space-y-6">
      <h2 class="text-3xl md:text-4xl font-bold">What You Get</h2>
      <p class="text-lg text-gray-600">
        A complete ISO 27001 and ISO 42001 implementation framework designed to take you from gap analysis to certification readiness with clarity and structure.
      </p>
    </div>

    <!-- Grid -->
    <div class="grid md:grid-cols-2 gap-10">
      <div class="flex gap-4">
        <div class="text-blue-600 mt-1">
          <!-- Check Icon -->
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <h3 class="font-semibold text-gray-900">Full ISO 27001 and ISO 42001 ISMS Documentation Pack</h3>
          <p class="text-gray-600 text-sm mt-1">
            Complete policy framework structured for certification body review.
          </p>
        </div>
      </div>

      <div class="flex gap-4">
        <div class="text-blue-600 mt-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <h3 class="font-semibold text-gray-900">Risk Assessment & Statement of Applicability</h3>
          <p class="text-gray-600 text-sm mt-1">
            Structured risk methodology aligned with ISO 27001 Annex A controls.
          </p>
        </div>
      </div>

      <div class="flex gap-4">
        <div class="text-blue-600 mt-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <h3 class="font-semibold text-gray-900">Mandatory Policies, Procedures & Records</h3>
          <p class="text-gray-600 text-sm mt-1">
            Audit-ready documentation covering required clauses and controls.
          </p>
        </div>
      </div>

      <div class="flex gap-4">
        <div class="text-blue-600 mt-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <h3 class="font-semibold text-gray-900">Internal Audit & Corrective Action Support</h3>
          <p class="text-gray-600 text-sm mt-1">
            Preparation for Stage 1 and Stage 2 certification audits.
          </p>
        </div>
      </div>

      <div class="flex gap-4">
        <div class="text-blue-600 mt-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <h3 class="font-semibold text-gray-900">Evidence Templates & Audit-Aligned Records</h3>
          <p class="text-gray-600 text-sm mt-1">
            Pre-structured templates mapped to certification expectations.
          </p>
        </div>
      </div>

      <div class="flex gap-4">
        <div class="text-blue-600 mt-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <h3 class="font-semibold text-gray-900">Certification Readiness Checklist</h3>
          <p class="text-gray-600 text-sm mt-1">
            Clear pre-audit verification framework before certification body review.
          </p>
        </div>
      </div>

      <div class="flex gap-4">
        <div class="text-blue-600 mt-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <h3 class="font-semibold text-gray-900">Auditor Q&A Preparation</h3>
          <p class="text-gray-600 text-sm mt-1">
            Practical preparation sessions to ensure confident audit interviews.
          </p>
        </div>
      </div>

      <div class="flex gap-4">
        <div class="text-blue-600 mt-1">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="1.8"
          >
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <h3 class="font-semibold text-gray-900">Optional Ongoing ISMS Support</h3>
          <p class="text-gray-600 text-sm mt-1">
            Continuous improvement and annual surveillance audit assistance.
          </p>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- SECONDARY CTA -->
<section class="bg-white py-16 text-center">
  <div class="max-w-4xl mx-auto px-6">
    <p class="text-xl font-semibold text-gray-800 mb-4">
      Most clients reach certification readiness in 8–12 weeks.
    </p>
    <button
      routerLink="/gap-check/intro"
      class="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 cursor-pointer"
    >
      Start Your Gap Check
    </button>
  </div>
</section>
